import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../config';

function debounce(fn, ms) {
  var timer;
  return function () {
    var args = arguments;
    var ctx = this;
    clearTimeout(timer);
    timer = setTimeout(function () { fn.apply(ctx, args); }, ms);
  };
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function SearchBar() {
  var [query, setQuery] = useState('');
  var [results, setResults] = useState(null);
  var [loading, setLoading] = useState(false);
  var [open, setOpen] = useState(false);
  var [expanded, setExpanded] = useState(false);
  var wrapperRef = useRef(null);
  var inputRef = useRef(null);
  var queryRef = useRef('');

  // Close on outside click
  useEffect(function () {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setExpanded(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return function () { document.removeEventListener('mousedown', handleClick); };
  }, []);

  // Close on Escape
  useEffect(function () {
    function handleKey(e) {
      if (e.key === 'Escape') {
        setOpen(false);
        setExpanded(false);
        if (inputRef.current) inputRef.current.blur();
      }
    }
    document.addEventListener('keydown', handleKey);
    return function () { document.removeEventListener('keydown', handleKey); };
  }, []);

  // Debounced search — 400ms delay, min 2 chars
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var doSearch = useCallback(debounce(function (q) {
    if (q.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(API_BASE_URL + '/api/search?q=' + encodeURIComponent(q) + '&limit=5')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        // Only show if user hasn't cleared the input while fetch was in-flight
        if (queryRef.current.length < 2) return;
        setResults(data);
        setLoading(false);
        setOpen(true);
      })
      .catch(function () { setLoading(false); });
  }, 400), []);

  function handleChange(e) {
    var val = e.target.value;
    setQuery(val);
    queryRef.current = val.trim();
    if (val.trim().length < 2) {
      setResults(null);
      setOpen(false);
      setLoading(false);
    } else {
      doSearch(val.trim());
    }
  }

  function handleResultClick() {
    setOpen(false);
    setExpanded(false);
    setQuery('');
    setResults(null);
  }

  function handleToggle() {
    setExpanded(true);
    setTimeout(function () {
      if (inputRef.current) inputRef.current.focus();
    }, 50);
  }

  var hasArticles = results && results.articles && results.articles.length > 0;
  var hasGuides = results && results.guides && results.guides.length > 0;
  var hasResults = hasArticles || hasGuides;
  var noResults = results && !hasResults && query.length >= 2;

  return (
    <div className={'search-bar-wrapper' + (expanded ? ' search-bar-expanded' : '')} ref={wrapperRef}>
      {/* Mobile: icon-only toggle */}
      <button
        className="search-bar-toggle"
        onClick={handleToggle}
        type="button"
        aria-label="Open search"
      >
        <SearchIcon />
      </button>

      {/* Desktop: always visible. Mobile: visible when expanded */}
      <div className="search-bar-input-wrap">
        <span className="search-bar-icon"><SearchIcon /></span>
        <input
          ref={inputRef}
          type="text"
          className="search-bar-input"
          placeholder="Search..."
          value={query}
          onChange={handleChange}
          onFocus={function () { if (results) setOpen(true); }}
          aria-label="Search articles and guides"
        />
        {loading && <span className="search-bar-spinner" aria-hidden="true" />}
        {expanded && (
          <button
            className="search-bar-close"
            onClick={function () { setExpanded(false); setOpen(false); setQuery(''); setResults(null); }}
            type="button"
            aria-label="Close search"
          >
            ✕
          </button>
        )}
      </div>

      {open && (
        <div className="search-bar-dropdown">
          {noResults && (
            <div className="search-bar-empty">No results for &ldquo;{query}&rdquo;</div>
          )}
          {hasArticles && (
            <div className="search-bar-section">
              <div className="search-bar-section-label">Articles</div>
              {results.articles.map(function (a) {
                return (
                  <Link key={a.slug} to={'/blog/' + a.slug} className="search-bar-result" onClick={handleResultClick}>
                    <span className="search-bar-result-tag">{a.tag}</span>
                    <span className="search-bar-result-title">{a.title}</span>
                  </Link>
                );
              })}
            </div>
          )}
          {hasGuides && (
            <div className="search-bar-section">
              <div className="search-bar-section-label">Guides</div>
              {results.guides.map(function (g) {
                return (
                  <Link key={g.slug} to={'/learn/' + g.slug} className="search-bar-result" onClick={handleResultClick}>
                    <span className="search-bar-result-tag">{g.tag}</span>
                    <span className="search-bar-result-title">{g.title}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
